import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from '@mui/material';
import { Bar, BarChart, CartesianGrid, LabelList, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { invoiceService } from '../../services/api';
import { INK, MUTED, RULE, formatINR, prefersReducedMotion, Section, axisProps } from '../analytics/analyticsUi';

// One hue, light for last month and dark for this month, so each pair reads as before and after
const PREVIOUS_COLOR = '#9ec5f4';
const CURRENT_COLOR = '#256abf';
const UP_COLOR = '#0D7332';
const DOWN_COLOR = '#C2410C';
const ROW_HEIGHT = 46;

const compactINR = (value) => {
  if (Math.abs(value) >= 10000000) return `₹${(value / 10000000).toFixed(1)}Cr`;
  if (Math.abs(value) >= 100000) return `₹${(value / 100000).toFixed(1)}L`;
  if (Math.abs(value) >= 1000) return `₹${Math.round(value / 1000)}k`;
  return `₹${Math.round(value)}`;
};
const shortMonth = (key) => new Date(`${key}-01T00:00:00`).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' });
const sumValues = (obj) => Object.values(obj || {}).reduce((s, v) => s + v, 0);

// Change from last month as { text, color }; a plan with nothing last month is "New"
const changeOf = (previous, current) => {
  if (!previous && !current) return { text: '–', color: MUTED };
  if (!previous) return { text: 'New', color: UP_COLOR };
  const percent = ((current - previous) / previous) * 100;
  if (Math.abs(percent) < 0.05) return { text: '0%', color: MUTED };
  return { text: `${percent > 0 ? '+' : '−'}${Math.abs(percent).toFixed(1)}%`, color: percent > 0 ? UP_COLOR : DOWN_COLOR };
};
// "+₹12,000 (+5.0%)", or just "New" / "–"
const changeText = (previous, current) => {
  const change = changeOf(previous, current);
  if (!previous) return change.text;
  return `${current >= previous ? '+' : '−'}${formatINR(Math.abs(current - previous))} (${change.text})`;
};

// Change label to the right of each plan's pair of bars
const ChangeLabel = ({ x, y, width, height, index, rows }) => {
  const row = rows[index];
  if (!row) return null;
  const { text, color } = changeOf(row.previous, row.current);
  return (
    <text x={x + width + 8} y={y + height / 2} dominantBaseline="central" fontSize={12} fontWeight={600} fill={color}>
      {text}
    </text>
  );
};

const ComparisonTooltip = ({ active, payload, months }) => {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  const line = (label, value, color) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: color, flexShrink: 0 }} />
      <Typography variant="body2" sx={{ color: INK, flexGrow: 1 }}>{label}</Typography>
      <Typography variant="body2" sx={{ color: INK, fontWeight: 600, ml: 2 }}>{formatINR(value)}</Typography>
    </Box>
  );
  return (
    <Paper sx={{ px: 1.5, py: 1, borderRadius: 1.5, boxShadow: '0 4px 16px rgba(31, 42, 61, 0.14)', minWidth: 240, fontVariantNumeric: 'tabular-nums' }}>
      <Typography variant="caption" sx={{ color: MUTED, display: 'block', mb: 0.5 }}>{row.name}</Typography>
      {line(months.previous, row.previous, PREVIOUS_COLOR)}
      {line(months.current, row.current, CURRENT_COLOR)}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mt: 0.75, pt: 0.75, borderTop: `1px solid ${RULE}` }}>
        <Typography variant="body2" sx={{ color: INK }}>Change</Typography>
        <Typography variant="body2" sx={{ color: changeOf(row.previous, row.current).color, fontWeight: 600 }}>
          {changeText(row.previous, row.current)}
        </Typography>
      </Box>
      {row.notInvoiced > 0 && (
        <Typography variant="caption" sx={{ color: MUTED, display: 'block', mt: 0.5 }}>
          {row.notInvoiced >= row.current ? `${months.current} not invoiced yet` : `${formatINR(row.notInvoiced)} of ${months.current} not invoiced yet`}
        </Typography>
      )}
    </Paper>
  );
};

// Plan fees (before GST) for last month and this month; the dashboard loads it once for this chart and its figure
export const usePlanRevenue = (enabled) => {
  const [state, setState] = useState({ data: null, error: false });
  useEffect(() => {
    if (!enabled) return;
    invoiceService.planRevenue(2)
      .then((response) => setState({ data: response.data, error: false }))
      .catch(() => setState({ data: null, error: true }));
  }, [enabled]);
  return state;
};

// This month's plan revenue, the same total the chart shows
export const currentPlanRevenue = (data) => sumValues(data?.months?.[data.months.length - 1]?.plans);

// Admin dashboard: plan fees (before GST) last month against this month, plan by plan
const PlanRevenueChart = ({ data, error }) => {
  const [view, setView] = useState('chart');

  const { rows, months, totals } = useMemo(() => {
    if (!data || data.months.length < 2) return { rows: [], months: {}, totals: {} };
    const [previous, current] = data.months;
    return {
      rows: data.plans
        .map((p) => ({
          key: p.key,
          name: p.name,
          previous: previous.plans[p.key] || 0,
          current: current.plans[p.key] || 0,
          notInvoiced: current.notInvoiced[p.key] || 0
        }))
        .filter((r) => r.previous || r.current)
        .sort((a, b) => b.current - a.current || b.previous - a.previous),
      months: { previous: shortMonth(previous.key), current: shortMonth(current.key) },
      totals: { previous: sumValues(previous.plans), current: sumValues(current.plans) }
    };
  }, [data]);

  return (
    <Section
      title="Plan revenue per month"
      description="Plan fees before GST, last month against this month. Quarterly and yearly plans count as their monthly share. Includes fees that are due but not yet invoiced."
      action={(
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
          <ToggleButtonGroup size="small" exclusive value={view} onChange={(e, v) => v && setView(v)} aria-label="Plan revenue view">
            <ToggleButton value="chart" sx={{ textTransform: 'none', px: 1.25 }}>Chart</ToggleButton>
            <ToggleButton value="table" sx={{ textTransform: 'none', px: 1.25 }}>Table</ToggleButton>
          </ToggleButtonGroup>
          <Button size="small" component={RouterLink} to="/admin/payments" sx={{ textTransform: 'none', display: { xs: 'none', sm: 'inline-flex' } }}>
            Payments
          </Button>
        </Box>
      )}
    >
      {error ? (
        <Typography variant="body2" sx={{ color: MUTED }}>Plan revenue could not be loaded. Refresh the page to try again.</Typography>
      ) : !data ? (
        <Skeleton variant="rounded" height={300} />
      ) : !rows.length ? (
        <Typography variant="body2" sx={{ color: MUTED }}>No plan fees last month or this month.</Typography>
      ) : (
        <>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 2, sm: 5 }, mb: 2.5, fontVariantNumeric: 'tabular-nums' }}>
            {[
              { label: months.previous, value: formatINR(totals.previous), color: INK },
              { label: months.current, value: formatINR(totals.current), color: INK },
              { label: 'Change', value: changeText(totals.previous, totals.current), color: changeOf(totals.previous, totals.current).color }
            ].map((figure) => (
              <Box key={figure.label}>
                <Typography variant="caption" sx={{ color: MUTED, display: 'block' }}>{figure.label}</Typography>
                <Typography sx={{ color: figure.color, fontWeight: 600, fontSize: '1.25rem' }}>{figure.value}</Typography>
              </Box>
            ))}
          </Box>

          {view === 'chart' ? (
            <Box
              sx={{ height: rows.length * ROW_HEIGHT + 64 }}
              role="img"
              aria-label={`Bar chart comparing plan revenue for ${months.previous} and ${months.current}, by plan`}
            >
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={rows} layout="vertical" margin={{ top: 0, right: 64, left: 0, bottom: 0 }} barGap={2} barCategoryGap="22%">
                  <CartesianGrid stroke={RULE} horizontal={false} />
                  <XAxis type="number" {...axisProps} tickFormatter={compactINR} />
                  <YAxis type="category" dataKey="name" {...axisProps} axisLine={false} width={170} interval={0} tick={{ fill: INK, fontSize: 13 }} />
                  <Tooltip content={<ComparisonTooltip months={months} />} cursor={{ fill: 'rgba(31, 42, 61, 0.04)' }} />
                  <Legend verticalAlign="top" align="left" iconType="square" iconSize={10} wrapperStyle={{ fontSize: 13, color: MUTED, paddingBottom: 8 }} />
                  <Bar dataKey="previous" name={months.previous} fill={PREVIOUS_COLOR} radius={[0, 3, 3, 0]} isAnimationActive={!prefersReducedMotion()} />
                  <Bar dataKey="current" name={months.current} fill={CURRENT_COLOR} radius={[0, 3, 3, 0]} isAnimationActive={!prefersReducedMotion()}>
                    <LabelList dataKey="current" content={<ChangeLabel rows={rows} />} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>
          ) : (
            <TableContainer sx={{ mx: { xs: -2, sm: -3 }, width: 'auto' }}>
              <Table size="small" sx={{ minWidth: 520 }}>
                <TableHead>
                  <TableRow>
                    {['Plan', months.previous, months.current, 'Change'].map((label, i) => (
                      <TableCell
                        key={label}
                        align={i ? 'right' : 'left'}
                        sx={{ color: MUTED, fontWeight: 500, borderColor: RULE, whiteSpace: 'nowrap', pl: i ? undefined : { xs: 2, sm: 3 }, pr: i === 3 ? { xs: 2, sm: 3 } : undefined }}
                      >
                        {label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[...rows, { key: 'total', name: 'Total', ...totals }].map((r) => (
                    <TableRow key={r.key} sx={{ '& td': { borderColor: RULE, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', fontWeight: r.key === 'total' ? 600 : 400 } }}>
                      <TableCell sx={{ pl: { xs: 2, sm: 3 }, color: INK }}>{r.name}</TableCell>
                      <TableCell align="right" sx={{ color: r.previous ? INK : MUTED }}>{r.previous ? formatINR(r.previous) : '–'}</TableCell>
                      <TableCell align="right" sx={{ color: r.current ? INK : MUTED }}>{r.current ? formatINR(r.current) : '–'}</TableCell>
                      <TableCell align="right" sx={{ color: changeOf(r.previous, r.current).color, pr: { xs: 2, sm: 3 } }}>
                        {changeOf(r.previous, r.current).text}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}
    </Section>
  );
};

export default PlanRevenueChart;
